import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useUrlParams } from '../hooks/useUrlState';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';
import {
  api,
  DeliverableRollupRow,
  DeliverableRollupBucket,
} from '../lib/api';
import { fmtCurrency } from '../lib/fmt';
import { downloadCsv } from '../lib/csv';
import {
  SectionLabel, Card, CardHeader, MetricCard, Badge,
  Table, Th, Tr, Td, Spinner, EmptyState, ActionButton,
  FilterBar,
} from '../components/ui';

// ─── Deliverable rollup ─────────────────────────────────────────────────────
// Shipped + WIP value per deliverable over a custom date range, filtered by
// issue type and optionally bucketed by day/week/month/quarter/year.
// Shipped attributes by completed_date; WIP attributes by due_date (so a
// deliverable planned for March that's still in-flight lands in March WIP).

type IssueTypeFilter = 'all' | 'deliverables' | 'overheads';
type GroupBy = 'none' | 'day' | 'week' | 'month' | 'quarter' | 'year';

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoMonthsAgo(n: number): string {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() - n);
  d.setUTCDate(1);
  return d.toISOString().slice(0, 10);
}

const DEFAULTS = {
  start:      isoMonthsAgo(3),
  end:        isoToday(),
  issue_type: 'all' as IssueTypeFilter,
  group_by:   'month' as GroupBy,
  search:     '',
};

export default function AdminDeliverableRollupPage() {
  // `applied` holds the filter snapshot the query is using.
  // `draft` holds in-flight edits; nothing refetches until the user clicks
  // Apply. Clear resets both to defaults.
  const [applied, setApplied] = useUrlParams(DEFAULTS);
  const [draft, setDraft] = useState(applied);

  // If the URL changes externally (browser back/forward, nav tab), pull the
  // new applied state into the draft so the inputs reflect reality.
  useEffect(() => { setDraft(applied); }, [applied.start, applied.end, applied.issue_type, applied.group_by, applied.search]);  // eslint-disable-line react-hooks/exhaustive-deps

  const start      = applied.start;
  const end        = applied.end;
  const issueType  = applied.issue_type as IssueTypeFilter;
  const groupBy    = applied.group_by   as GroupBy;
  const search     = applied.search;

  const dirty =
       draft.start      !== applied.start
    || draft.end        !== applied.end
    || draft.issue_type !== applied.issue_type
    || draft.group_by   !== applied.group_by
    || draft.search     !== applied.search;

  const apply = () => setApplied(draft);
  const clear = () => { setDraft(DEFAULTS); setApplied(DEFAULTS); };

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    () => api.admin.deliverableRollup({ start, end, issue_type: issueType, group_by: groupBy }),
    [start, end, issueType, groupBy],
  );
  void refetch;

  const filtered: DeliverableRollupBucket[] = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data.buckets;
    return data.buckets
      .map((b) => ({
        ...b,
        deliverables: b.deliverables.filter((d) =>
          d.jira_issue_key.toLowerCase().includes(needle)
          || (d.summary ?? '').toLowerCase().includes(needle)
          || (d.account_name ?? '').toLowerCase().includes(needle)
          || (d.engagement_jira_issue_key ?? '').toLowerCase().includes(needle)
          || (d.engagement_summary ?? '').toLowerCase().includes(needle)
          || (d.delivery_manager_display_name ?? '').toLowerCase().includes(needle)
          || (d.solution_architect_display_name ?? '').toLowerCase().includes(needle),
        ),
      }))
      .filter((b) => b.deliverables.length > 0);
  }, [data, search]);

  function exportCsv() {
    if (!data) return;
    const rows: Array<Array<unknown>> = [[
      'Bucket', 'Issue key', 'Summary', 'Type', 'Status', 'Completed', 'Due',
      'Engagement key', 'Engagement', 'Account', 'DM', 'SA',
      'Hours', 'Rate', 'Fixed fee', 'Shipped', 'WIP', 'Canceled', 'Total',
    ]];
    for (const b of data.buckets) {
      for (const r of b.deliverables) {
        rows.push([
          b.bucket_label,
          r.jira_issue_key, r.summary, r.issue_type, r.status,
          r.completed_date ?? '', r.due_date ?? '',
          r.engagement_jira_issue_key ?? '',
          r.engagement_summary ?? '',
          r.account_name ?? '',
          r.delivery_manager_display_name ?? '',
          r.solution_architect_display_name ?? '',
          r.hours.toFixed(2), r.billable_rate.toFixed(2),
          r.is_fixed_fee ? 'yes' : 'no',
          r.shipped_value.toFixed(2), r.wip_value.toFixed(2),
          r.canceled_value.toFixed(2), r.total_value.toFixed(2),
        ]);
      }
    }
    downloadCsv(`deliverable-rollup-${start}-to-${end}.csv`, rows);
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Deliverable rollup</SectionLabel>
        <span style={{ marginLeft: 'auto' }}>
          <ActionButton onClick={exportCsv} variant="default" disabled={!data}>Export CSV</ActionButton>
        </span>
      </div>

      <Card>
        <CardHeader>
          <FilterBar
            isFetching={isFetching}
            dirty={dirty}
            onApply={apply}
            onClear={clear}
          >
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              From
              <input
                type="date" value={draft.start}
                onChange={(e) => setDraft({ ...draft, start: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter' && dirty) apply(); }}
                style={dateInputStyle}
              />
            </label>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              To
              <input
                type="date" value={draft.end}
                onChange={(e) => setDraft({ ...draft, end: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter' && dirty) apply(); }}
                style={dateInputStyle}
              />
            </label>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              Type
              <select
                value={draft.issue_type}
                onChange={(e) => setDraft({ ...draft, issue_type: e.target.value as IssueTypeFilter })}
                style={selectStyle}
              >
                <option value="all">All</option>
                <option value="deliverables">Deliverables only</option>
                <option value="overheads">Overheads only</option>
              </select>
            </label>
            <label style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              Group by
              <select
                value={draft.group_by}
                onChange={(e) => setDraft({ ...draft, group_by: e.target.value as GroupBy })}
                style={selectStyle}
              >
                <option value="none">Aggregated</option>
                <option value="day">Day</option>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
              </select>
            </label>
            <span>
              <input
                type="search"
                placeholder="Filter by issue, account, owner…"
                value={draft.search}
                onChange={(e) => setDraft({ ...draft, search: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter' && dirty) apply(); }}
                style={{
                  fontSize: 11, padding: '4px 8px', width: 260,
                  border: '0.5px solid var(--color-border-strong)',
                  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
                }}
              />
            </span>
          </FilterBar>
        </CardHeader>
      </Card>

      {isLoading ? <Spinner /> :
       error    ? <EmptyState message={error} /> :
       !data    ? null : (
        <div style={{
          opacity: isFetching ? 0.4 : 1,
          pointerEvents: isFetching ? 'none' : undefined,
          transition: 'opacity 120ms',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10, marginTop: 10 }}>
            <MetricCard label="Shipped" value={fmtCurrency(data.totals.shipped_total)} sub={`${data.totals.deliverable_count} deliverable${data.totals.deliverable_count === 1 ? '' : 's'}`} />
            <MetricCard label="WIP" value={fmtCurrency(data.totals.wip_total)} sub="Planned by due date" />
            <MetricCard
              label="Canceled (forfeit)"
              value={fmtCurrency(data.totals.canceled_total)}
              sub="Never shipped"
              accent={data.totals.canceled_total > 0 ? 'amber' : undefined}
            />
            <MetricCard label="Grand total" value={fmtCurrency(data.totals.grand_total)} accent="blue" />
            <MetricCard label="Range" value={`${start} → ${end}`} sub={groupByLabel(groupBy)} />
          </div>

          {filtered.length === 0 ? (
            <Card><EmptyState message={search.trim() ? 'No matches.' : 'Nothing in this range.'} /></Card>
          ) : (
            <div style={{ marginTop: 10 }}>
              <button
                onClick={refetch}
                style={{ display: 'none' }}
              />
              {filtered.map((b) => (
                <BucketBlock key={b.bucket_key} bucket={b} showBucketHeader={groupBy !== 'none'} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function groupByLabel(g: GroupBy): string {
  return g === 'none' ? 'Aggregated'
       : g === 'day'  ? 'By day'
       : g === 'week' ? 'By week'
       : g === 'month'? 'By month'
       : g === 'quarter' ? 'By quarter'
       : 'By year';
}

// Group deliverables by engagement (synthetic key for those without one).
// Each group carries pre-summed aggregates so the parent row can render
// without re-walking children every paint.
type EngagementBundle = {
  key: string;
  engagementKey: string | null;
  engagementSummary: string | null;
  deliverables: DeliverableRollupRow[];
  hours: number;
  shipped: number;
  wip: number;
  canceled: number;
  total: number;
};

function groupByEngagement(rows: DeliverableRollupRow[]): EngagementBundle[] {
  const m = new Map<string, EngagementBundle>();
  for (const r of rows) {
    const key = r.engagement_jira_issue_id ?? '__orphan__';
    let g = m.get(key);
    if (!g) {
      g = {
        key,
        engagementKey:     r.engagement_jira_issue_key ?? null,
        engagementSummary: r.engagement_summary ?? null,
        deliverables: [],
        hours: 0, shipped: 0, wip: 0, canceled: 0, total: 0,
      };
      m.set(key, g);
    }
    g.deliverables.push(r);
    g.hours    += r.hours;
    g.shipped  += r.shipped_value;
    g.wip      += r.wip_value;
    g.canceled += r.canceled_value;
    g.total    += r.total_value;
  }
  // Engagements ordered by total value desc; orphans (no engagement) last.
  return [...m.values()].sort((a, b) => {
    if (a.key === '__orphan__') return 1;
    if (b.key === '__orphan__') return -1;
    return b.total - a.total;
  });
}

function BucketBlock({ bucket, showBucketHeader }: { bucket: DeliverableRollupBucket; showBucketHeader: boolean }) {
  const groups = useMemo(() => groupByEngagement(bucket.deliverables), [bucket.deliverables]);

  return (
    <Card>
      {showBucketHeader && (
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 12,
          padding: '10px 16px',
          borderBottom: '0.5px solid var(--color-border)',
          background: 'var(--color-bg)',
        }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{bucket.bucket_label}</span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
            {bucket.deliverable_count} deliverable{bucket.deliverable_count === 1 ? '' : 's'}
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 16, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
            <span>Shipped <strong>{fmtCurrency(bucket.shipped_total)}</strong></span>
            <span>WIP <strong>{fmtCurrency(bucket.wip_total)}</strong></span>
            <span style={{ color: bucket.canceled_total > 0 ? 'var(--color-red-text)' : undefined }}>
              Canceled <strong>{fmtCurrency(bucket.canceled_total)}</strong>
            </span>
            <span style={{ color: 'var(--color-blue)' }}>Total <strong>{fmtCurrency(bucket.grand_total)}</strong></span>
          </span>
        </div>
      )}
      <Table>
        <thead>
          <tr>
            <Th>{' '}</Th>
            <Th>Engagement</Th>
            <Th>Issue</Th>
            <Th>Summary</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Account</Th>
            <Th>DM</Th>
            <Th>SA</Th>
            <Th right>Hours</Th>
            <Th right>Shipped</Th>
            <Th right>WIP</Th>
            <Th right>Canceled</Th>
            <Th right>Total</Th>
            <Th>Jira</Th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => <EngagementGroup key={g.key} group={g} />)}
        </tbody>
      </Table>
    </Card>
  );
}

function EngagementGroup({ group }: { group: EngagementBundle }) {
  const [open, setOpen] = useState(false);
  const { config } = useConfig();
  const engUrl = group.engagementKey ? jiraIssueUrl(config, group.engagementKey) : null;
  // Deliverables shown in jira-issue-key order — stable, predictable.
  const sortedDeliverables = useMemo(
    () => [...group.deliverables].sort((a, b) => a.jira_issue_key.localeCompare(b.jira_issue_key)),
    [group.deliverables],
  );
  return (
    <>
      <Tr
        onClick={() => setOpen(!open)}
        style={{ cursor: 'pointer', background: 'var(--color-bg)' }}
      >
        <Td muted style={{ width: 20, fontSize: 11 }}>{open ? '▼' : '▶'}</Td>
        <Td bold style={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal' }}>
          {group.engagementKey ? (
            <>
              {engUrl ? (
                <a
                  href={engUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: 'var(--color-blue)' }}
                >
                  {group.engagementKey} ↗
                </a>
              ) : group.engagementKey}
              {group.engagementSummary && (
                <div style={{ fontSize: 10, color: 'var(--color-text-hint)', fontWeight: 400 }}>
                  {group.engagementSummary}
                </div>
              )}
            </>
          ) : (
            <span style={{ color: 'var(--color-text-muted)' }}>No engagement</span>
          )}
        </Td>
        <Td muted style={{ fontSize: 10 }}>
          {group.deliverables.length} item{group.deliverables.length === 1 ? '' : 's'}
        </Td>
        <Td>{' '}</Td>
        <Td>{' '}</Td>
        <Td>{' '}</Td>
        <Td>{' '}</Td>
        <Td>{' '}</Td>
        <Td>{' '}</Td>
        <Td right bold>{group.hours.toFixed(1)}</Td>
        <Td right bold>{group.shipped > 0 ? fmtCurrency(group.shipped) : '—'}</Td>
        <Td right bold>{group.wip > 0 ? fmtCurrency(group.wip) : '—'}</Td>
        <Td right bold style={{ color: group.canceled > 0 ? 'var(--color-red-text)' : undefined }}>
          {group.canceled > 0 ? fmtCurrency(group.canceled) : '—'}
        </Td>
        <Td right bold>{fmtCurrency(group.total)}</Td>
        <Td>{' '}</Td>
      </Tr>
      {open && sortedDeliverables.map((r) => (
        <DeliverableRow
          key={r.jira_issue_id}
          row={r}
          jiraBase={jiraIssueUrl(config, r.jira_issue_key)}
        />
      ))}
    </>
  );
}

function DeliverableRow({ row, jiraBase }: { row: DeliverableRollupRow; jiraBase: string | null }) {
  return (
    <Tr>
      <Td>{' '}</Td>
      <Td>{' '}</Td>
      <Td bold style={{ whiteSpace: 'nowrap', paddingLeft: 24 }}>{row.jira_issue_key}</Td>
      <Td muted style={{ fontSize: 11, maxWidth: 320, whiteSpace: 'normal' }}>{row.summary}</Td>
      <Td><Badge variant={row.issue_type === 'Overhead' ? 'amber' : 'gray'}>{row.issue_type}</Badge></Td>
      <Td><Badge variant={row.status === 'Completed' ? 'green' : 'gray'}>{row.status}</Badge></Td>
      <Td muted style={{ fontSize: 11 }}>{row.account_name ?? '—'}</Td>
      <Td muted style={{ fontSize: 11 }}>{row.delivery_manager_display_name ?? '—'}</Td>
      <Td muted style={{ fontSize: 11 }}>{row.solution_architect_display_name ?? '—'}</Td>
      <Td right muted>{row.hours.toFixed(1)}</Td>
      <Td right>{row.shipped_value > 0 ? fmtCurrency(row.shipped_value) : '—'}</Td>
      <Td right muted>{row.wip_value > 0 ? fmtCurrency(row.wip_value) : '—'}</Td>
      <Td right muted style={{ color: row.canceled_value > 0 ? 'var(--color-red-text)' : undefined }}>
        {row.canceled_value > 0 ? fmtCurrency(row.canceled_value) : '—'}
      </Td>
      <Td right bold>{fmtCurrency(row.total_value)}</Td>
      <Td>{jiraBase ? <a href={jiraBase} target="_blank" rel="noreferrer" style={{ color: 'var(--color-blue)', fontSize: 11 }}>Open ↗</a> : '—'}</Td>
    </Tr>
  );
}

const dateInputStyle: React.CSSProperties = {
  marginLeft: 6,
  fontSize: 11, padding: '4px 8px',
  border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
};

const selectStyle: React.CSSProperties = {
  marginLeft: 6,
  fontSize: 11, padding: '4px 8px',
  border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
};
